import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  User, 
  Camera, 
  Video, 
  Trophy, 
  MapPin, 
  Calendar, 
  Edit3, 
  Save, 
  X, 
  Plus,
  Upload,
  Settings,
  Eye,
  EyeOff,
  Star,
  Medal,
  Target,
  BarChart3,
  Instagram,
  Twitter,
  Facebook,
  Globe
} from 'lucide-react';

interface AthleteInfo {
  personalInfo: {
    name: string;
    email: string;
    dateOfBirth: string;
    location: string;
    nationality: string;
    height: string;
    weight: string;
    bio: string;
    phone: string;
  };
  sportsInfo: {
    primarySport: string;
    position: string;
    experience: string;
    club: string;
    coach: string;
    achievements: string[];
    specialties: string[];
  };
  mediaGallery: {
    profilePhoto: string;
    coverPhoto: string;
    photos: MediaItem[];
    videos: MediaItem[];
  };
  socialLinks: {
    instagram: string;
    twitter: string;
    facebook: string;
    tiktok: string;
    youtube: string;
    website: string;
  };
  privacySettings: {
    profileVisibility: 'public' | 'private' | 'followers';
    contactInfoVisible: boolean;
    statsVisible: boolean;
    mediaVisible: boolean;
  };
}

interface MediaItem {
  id: string;
  type: 'photo' | 'video';
  url: string;
  thumbnail?: string;
  title: string;
  description: string;
  category: 'training' | 'competition' | 'personal' | 'achievements';
  uploadDate: string;
  isPublic: boolean;
}
import PhotoUpload from '../components/media/PhotoUpload';
import PhotoGallery from '../components/media/PhotoGallery';

const AthleteProfile = () => {
  const { user, isAuthenticated, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'info' | 'media' | 'achievements' | 'settings'>('info');
  const [isEditing, setIsEditing] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadType, setUploadType] = useState<'photo' | 'video'>('photo');
  const [isSaving, setIsSaving] = useState(false);
  const [showPhotoUpload, setShowPhotoUpload] = useState(false);

  const [athleteInfo, setAthleteInfo] = useState<AthleteInfo>({
    personalInfo: {
      name: user?.name || '',
      email: user?.email || '',
      dateOfBirth: user?.date_of_birth || '',
      location: user?.location || '',
      nationality: '',
      height: '',
      weight: '',
      bio: user?.bio || '',
      phone: ''
    },
    sportsInfo: {
      primarySport: user?.sport || '',
      position: '',
      experience: '',
      club: '',
      coach: '',
      achievements: [],
      specialties: []
    },
    mediaGallery: {
      profilePhoto: user?.avatar_url || 'https://images.pexels.com/photos/3763188/pexels-photo-3763188.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      coverPhoto: 'https://images.pexels.com/photos/3621104/pexels-photo-3621104.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      photos: [],
      videos: []
    },
    socialLinks: {
      instagram: (user?.social_links as any)?.instagram || '',
      twitter: (user?.social_links as any)?.twitter || '',
      facebook: (user?.social_links as any)?.facebook || '',
      tiktok: (user?.social_links as any)?.tiktok || '',
      youtube: (user?.social_links as any)?.youtube || '',
      website: (user?.social_links as any)?.website || ''
    },
    privacySettings: {
      profileVisibility: 'public',
      contactInfoVisible: false,
      statsVisible: true,
      mediaVisible: true
    }
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    document.title = 'Mi Perfil de Atleta - FemSportNet';
  }, [isAuthenticated, navigate]);

  const handleSave = async () => {
    if (!user) return;
    
    setIsSaving(true);
    try {
      // Update profile in database
      await updateProfile({
        name: athleteInfo.personalInfo.name,
        bio: athleteInfo.personalInfo.bio,
        location: athleteInfo.personalInfo.location,
        sport: athleteInfo.sportsInfo.primarySport,
        date_of_birth: athleteInfo.personalInfo.dateOfBirth || null,
        avatar_url: athleteInfo.mediaGallery.profilePhoto,
        social_links: athleteInfo.socialLinks
      });
      
      setIsEditing(false);
      
      // Show success message
      alert('Perfil actualizado exitosamente');
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Error al guardar el perfil. Intenta nuevamente.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      Array.from(files).forEach(file => {
        const url = URL.createObjectURL(file);
        const newItem: MediaItem = {
          id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
          type: uploadType,
          url: url,
          thumbnail: uploadType === 'video' ? url : undefined,
          title: file.name,
          description: '',
          category: 'training',
          uploadDate: new Date().toISOString(),
          isPublic: true
        };

        setAthleteInfo(prev => ({
          ...prev,
          mediaGallery: {
            ...prev.mediaGallery,
            [uploadType === 'photo' ? 'photos' : 'videos']: [
              ...prev.mediaGallery[uploadType === 'photo' ? 'photos' : 'videos'],
              newItem
            ]
          }
        }));
      });
      setShowUploadModal(false);
    }
  };

  const handleProfilePhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setAthleteInfo(prev => ({
        ...prev,
        mediaGallery: {
          ...prev.mediaGallery,
          profilePhoto: url
        }
      }));
    }
  };

  const handleCoverPhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setAthleteInfo(prev => ({
        ...prev,
        mediaGallery: {
          ...prev.mediaGallery,
          coverPhoto: url
        }
      }));
    }
  };

  const tabs = [
    { id: 'info', label: 'Información Personal', icon: <User size={18} /> },
    { id: 'media', label: 'Galería Multimedia', icon: <Camera size={18} /> },
    { id: 'achievements', label: 'Logros Deportivos', icon: <Trophy size={18} /> },
    { id: 'settings', label: 'Configuración', icon: <Settings size={18} /> }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden mb-8">
            {/* Cover Photo */}
            <div className="h-64 relative">
              <img 
                src={athleteInfo.mediaGallery.coverPhoto} 
                alt="Foto de portada"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
              {isEditing && (
                <label className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm text-white p-2 rounded-full hover:bg-white/30 transition-colors cursor-pointer">
                  <Camera size={20} />
                  <input 
                    type="file" 
                    accept="image/*" 
                    onChange={handleCoverPhotoUpload}
                    className="hidden" 
                  />
                </label>
              )}
            </div>

            {/* Profile Info */}
            <div className="p-6">
              <div className="flex flex-col md:flex-row md:items-end -mt-20 relative z-10">
                <div className="flex-shrink-0 mb-4 md:mb-0 md:mr-6">
                  <div className="relative">
                    <div className="w-32 h-32 rounded-full border-4 border-white dark:border-gray-800 overflow-hidden bg-white">
                      <img 
                        src={athleteInfo.mediaGallery.profilePhoto} 
                        alt="Foto de perfil"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    {isEditing && (
                      <label className="absolute bottom-2 right-2 bg-green-500 text-white p-2 rounded-full hover:bg-green-600 transition-colors cursor-pointer">
                        <Camera size={16} />
                        <input 
                          type="file" 
                          accept="image/*" 
                          onChange={handleProfilePhotoUpload}
                          className="hidden" 
                        />
                      </label>
                    )}
                  </div>
                </div>

                <div className="flex-grow">
                  <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                    {athleteInfo.personalInfo.name || 'Tu Nombre'}
                  </h1>
                  <div className="flex flex-wrap items-center gap-4 text-gray-600 dark:text-gray-300 mb-4">
                    <div className="flex items-center">
                      <Target className="h-4 w-4 mr-1" />
                      <span>{athleteInfo.sportsInfo.primarySport || 'Tu Deporte'}</span>
                    </div>
                    {athleteInfo.personalInfo.location && (
                      <div className="flex items-center">
                        <MapPin className="h-4 w-4 mr-1" />
                        <span>{athleteInfo.personalInfo.location}</span>
                      </div>
                    )}
                    {athleteInfo.sportsInfo.club && (
                      <div className="flex items-center">
                        <Star className="h-4 w-4 mr-1" />
                        <span>{athleteInfo.sportsInfo.club}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={() => setIsEditing(!isEditing)}
                      disabled={isSaving}
                      className="flex items-center px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50"
                    >
                      <Edit3 size={16} className="mr-2" />
                      {isEditing ? 'Cancelar' : 'Editar Perfil'}
                    </button>
                    {isEditing && (
                      <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50"
                      >
                        <Save size={16} className="mr-2" />
                        {isSaving ? 'Guardando...' : 'Guardar Cambios'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation Tabs */}
            <div className="border-t border-gray-200 dark:border-gray-700">
              <nav className="flex">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`flex items-center px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === tab.id
                        ? 'border-green-500 text-green-600 dark:text-green-400'
                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    {tab.icon}
                    <span className="ml-2">{tab.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            {activeTab === 'info' && (
              <div className="space-y-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                  Información Personal y Deportiva
                </h2>

                {/* Información Personal */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Datos Personales
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Nombre Completo
                      </label>
                      <input
                        type="text"
                        value={athleteInfo.personalInfo.name}
                        onChange={(e) => setAthleteInfo(prev => ({
                          ...prev,
                          personalInfo: { ...prev.personalInfo, name: e.target.value }
                        }))}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-gray-600"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        value={athleteInfo.personalInfo.email}
                        onChange={(e) => setAthleteInfo(prev => ({
                          ...prev,
                          personalInfo: { ...prev.personalInfo, email: e.target.value }
                        }))}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-gray-600"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Fecha de Nacimiento
                      </label>
                      <input
                        type="date"
                        value={athleteInfo.personalInfo.dateOfBirth}
                        onChange={(e) => setAthleteInfo(prev => ({
                          ...prev,
                          personalInfo: { ...prev.personalInfo, dateOfBirth: e.target.value }
                        }))}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-gray-600"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Ubicación
                      </label>
                      <input
                        type="text"
                        value={athleteInfo.personalInfo.location}
                        onChange={(e) => setAthleteInfo(prev => ({
                          ...prev,
                          personalInfo: { ...prev.personalInfo, location: e.target.value }
                        }))}
                        disabled={!isEditing}
                        placeholder="Ciudad, País"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-gray-600"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Nacionalidad
                      </label>
                      <input
                        type="text"
                        value={athleteInfo.personalInfo.nationality}
                        onChange={(e) => setAthleteInfo(prev => ({
                          ...prev,
                          personalInfo: { ...prev.personalInfo, nationality: e.target.value }
                        }))}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-gray-600"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Teléfono
                      </label>
                      <input
                        type="tel"
                        value={athleteInfo.personalInfo.phone}
                        onChange={(e) => setAthleteInfo(prev => ({
                          ...prev,
                          personalInfo: { ...prev.personalInfo, phone: e.target.value }
                        }))}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-gray-600"
                      />
                    </div>
                  </div>

                  <div className="mt-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Biografía
                    </label>
                    <textarea
                      value={athleteInfo.personalInfo.bio}
                      onChange={(e) => setAthleteInfo(prev => ({
                        ...prev,
                        personalInfo: { ...prev.personalInfo, bio: e.target.value }
                      }))}
                      disabled={!isEditing}
                      rows={4}
                      placeholder="Cuéntanos sobre ti, tu historia deportiva y tus objetivos..."
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-gray-600"
                    />
                  </div>
                </div>

                {/* Información Deportiva */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Información Deportiva
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Deporte Principal
                      </label>
                      <input
                        type="text"
                        value={athleteInfo.sportsInfo.primarySport}
                        onChange={(e) => setAthleteInfo(prev => ({
                          ...prev,
                          sportsInfo: { ...prev.sportsInfo, primarySport: e.target.value }
                        }))}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-gray-600"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Posición/Especialidad
                      </label>
                      <input
                        type="text"
                        value={athleteInfo.sportsInfo.position}
                        onChange={(e) => setAthleteInfo(prev => ({
                          ...prev,
                          sportsInfo: { ...prev.sportsInfo, position: e.target.value }
                        }))}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-gray-600"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Años de Experiencia
                      </label>
                      <input
                        type="text"
                        value={athleteInfo.sportsInfo.experience}
                        onChange={(e) => setAthleteInfo(prev => ({
                          ...prev,
                          sportsInfo: { ...prev.sportsInfo, experience: e.target.value }
                        }))}
                        disabled={!isEditing}
                        placeholder="ej. 5 años"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-gray-600"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Club/Equipo Actual
                      </label>
                      <input
                        type="text"
                        value={athleteInfo.sportsInfo.club}
                        onChange={(e) => setAthleteInfo(prev => ({
                          ...prev,
                          sportsInfo: { ...prev.sportsInfo, club: e.target.value }
                        }))}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-gray-600"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Entrenador
                      </label>
                      <input
                        type="text"
                        value={athleteInfo.sportsInfo.coach}
                        onChange={(e) => setAthleteInfo(prev => ({
                          ...prev,
                          sportsInfo: { ...prev.sportsInfo, coach: e.target.value }
                        }))}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-gray-600"
                      />
                    </div>
                  </div>
                </div>

                {/* Redes Sociales */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Redes Sociales
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        <Instagram size={16} className="inline mr-2" />
                        Instagram
                      </label>
                      <input
                        type="url"
                        value={athleteInfo.socialLinks.instagram}
                        onChange={(e) => setAthleteInfo(prev => ({
                          ...prev,
                          socialLinks: { ...prev.socialLinks, instagram: e.target.value }
                        }))}
                        disabled={!isEditing}
                        placeholder="https://instagram.com/tu_usuario"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-gray-600"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        <Twitter size={16} className="inline mr-2" />
                        Twitter
                      </label>
                      <input
                        type="url"
                        value={athleteInfo.socialLinks.twitter}
                        onChange={(e) => setAthleteInfo(prev => ({
                          ...prev,
                          socialLinks: { ...prev.socialLinks, twitter: e.target.value }
                        }))}
                        disabled={!isEditing}
                        placeholder="https://twitter.com/tu_usuario"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-gray-600"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        <Facebook size={16} className="inline mr-2" />
                        Facebook
                      </label>
                      <input
                        type="url"
                        value={athleteInfo.socialLinks.facebook}
                        onChange={(e) => setAthleteInfo(prev => ({
                          ...prev,
                          socialLinks: { ...prev.socialLinks, facebook: e.target.value }
                        }))}
                        disabled={!isEditing}
                        placeholder="https://facebook.com/tu_usuario"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-gray-600"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        <Globe size={16} className="inline mr-2" />
                        Sitio Web Personal
                      </label>
                      <input
                        type="url"
                        value={athleteInfo.socialLinks.website}
                        onChange={(e) => setAthleteInfo(prev => ({
                          ...prev,
                          socialLinks: { ...prev.socialLinks, website: e.target.value }
                        }))}
                        disabled={!isEditing}
                        placeholder="https://tu-sitio-web.com"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-gray-600"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'media' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Galería Multimedia
                  </h2>
                  <button
                    onClick={() => setShowUploadModal(true)}
                    className="flex items-center px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                  >
                    <Plus size={16} className="mr-2" />
                    Subir Contenido
                  </button>
                  <button
                    onClick={() => setShowPhotoUpload(true)}
                    className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    <Camera size={16} className="mr-2" />
                    Subir Fotos
                  </button>
                </div>

                {/* Fotos */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Fotos ({athleteInfo.mediaGallery.photos.length})
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {athleteInfo.mediaGallery.photos.map((photo) => (
                      <div key={photo.id} className="relative group">
                        <img
                          src={photo.url}
                          alt={photo.title}
                          className="w-full h-32 object-cover rounded-lg"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 rounded-lg flex items-center justify-center">
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                            <button className="text-white p-2 hover:bg-white/20 rounded-full">
                              <Eye size={16} />
                            </button>
                          </div>
                        </div>
                        <div className="absolute top-2 right-2">
                          {photo.isPublic ? (
                            <Eye size={14} className="text-white bg-green-500 p-1 rounded-full" />
                          ) : (
                            <EyeOff size={14} className="text-white bg-gray-500 p-1 rounded-full" />
                          )}
                        </div>
                      </div>
                    ))}
                    
                    {athleteInfo.mediaGallery.photos.length === 0 && (
                      <div className="col-span-full text-center py-12 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
                        <Camera className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-500 dark:text-gray-400">
                          No has subido fotos aún. ¡Comparte tus mejores momentos deportivos!
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Videos */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Videos ({athleteInfo.mediaGallery.videos.length})
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {athleteInfo.mediaGallery.videos.map((video) => (
                      <div key={video.id} className="relative group">
                        <div className="aspect-video bg-gray-200 dark:bg-gray-700 rounded-lg overflow-hidden">
                          <video
                            src={video.url}
                            className="w-full h-full object-cover"
                            controls
                          />
                        </div>
                        <div className="absolute top-2 right-2">
                          {video.isPublic ? (
                            <Eye size={14} className="text-white bg-green-500 p-1 rounded-full" />
                          ) : (
                            <EyeOff size={14} className="text-white bg-gray-500 p-1 rounded-full" />
                          )}
                        </div>
                        <div className="mt-2">
                          <h4 className="font-medium text-gray-900 dark:text-white text-sm">
                            {video.title}
                          </h4>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {new Date(video.uploadDate).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    ))}
                    
                    {athleteInfo.mediaGallery.videos.length === 0 && (
                      <div className="col-span-full text-center py-12 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
                        <Video className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-500 dark:text-gray-400">
                          No has subido videos aún. ¡Muestra tus entrenamientos y competencias!
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Quick Photo Upload */}
                {showPhotoUpload && (
                  <div className="mt-6 p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-medium text-gray-900 dark:text-white">Subir Fotos Rápido</h4>
                      <button
                        onClick={() => setShowPhotoUpload(false)}
                        className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                      >
                        <X size={20} />
                      </button>
                    </div>
                    <PhotoUpload
                      onUpload={(files) => {
                        // Handle quick photo upload
                        console.log('Quick upload:', files);
                        setShowPhotoUpload(false);
                      }}
                      maxPhotos={3}
                      maxSizePerPhoto={5}
                    />
                  </div>
                )}
              </div>
            )}

            {activeTab === 'achievements' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Logros Deportivos
                  </h2>
                  <button className="flex items-center px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors">
                    <Plus size={16} className="mr-2" />
                    Agregar Logro
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* Estadísticas Rápidas */}
                  <div className="bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-lg p-6 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-yellow-100">Medallas de Oro</p>
                        <p className="text-3xl font-bold">12</p>
                      </div>
                      <Medal className="h-12 w-12 text-yellow-200" />
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-gray-400 to-gray-600 rounded-lg p-6 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-100">Medallas de Plata</p>
                        <p className="text-3xl font-bold">8</p>
                      </div>
                      <Medal className="h-12 w-12 text-gray-200" />
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-orange-400 to-orange-600 rounded-lg p-6 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-orange-100">Medallas de Bronce</p>
                        <p className="text-3xl font-bold">5</p>
                      </div>
                      <Medal className="h-12 w-12 text-orange-200" />
                    </div>
                  </div>
                </div>

                {/* Lista de Logros */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Historial de Logros
                  </h3>
                  
                  <div className="text-center py-12 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
                    <Trophy className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 dark:text-gray-400 mb-4">
                      No has agregado logros aún. ¡Comparte tus éxitos deportivos!
                    </p>
                    <button className="text-green-500 hover:text-green-600 font-medium">
                      Agregar tu primer logro
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'settings' && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Configuración de Privacidad
                </h2>

                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      Visibilidad del Perfil
                    </h3>
                    <div className="space-y-3">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="profileVisibility"
                          value="public"
                          checked={athleteInfo.privacySettings.profileVisibility === 'public'}
                          onChange={(e) => setAthleteInfo(prev => ({
                            ...prev,
                            privacySettings: { ...prev.privacySettings, profileVisibility: e.target.value as any }
                          }))}
                          className="mr-3"
                        />
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">Público</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Cualquier persona puede ver tu perfil
                          </p>
                        </div>
                      </label>

                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="profileVisibility"
                          value="followers"
                          checked={athleteInfo.privacySettings.profileVisibility === 'followers'}
                          onChange={(e) => setAthleteInfo(prev => ({
                            ...prev,
                            privacySettings: { ...prev.privacySettings, profileVisibility: e.target.value as any }
                          }))}
                          className="mr-3"
                        />
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">Solo Seguidores</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Solo tus seguidores pueden ver tu perfil completo
                          </p>
                        </div>
                      </label>

                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="profileVisibility"
                          value="private"
                          checked={athleteInfo.privacySettings.profileVisibility === 'private'}
                          onChange={(e) => setAthleteInfo(prev => ({
                            ...prev,
                            privacySettings: { ...prev.privacySettings, profileVisibility: e.target.value as any }
                          }))}
                          className="mr-3"
                        />
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">Privado</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Solo tú puedes ver tu perfil
                          </p>
                        </div>
                      </label>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      Información Visible
                    </h3>
                    <div className="space-y-3">
                      <label className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">Información de Contacto</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Email y teléfono visibles en tu perfil
                          </p>
                        </div>
                        <input
                          type="checkbox"
                          checked={athleteInfo.privacySettings.contactInfoVisible}
                          onChange={(e) => setAthleteInfo(prev => ({
                            ...prev,
                            privacySettings: { ...prev.privacySettings, contactInfoVisible: e.target.checked }
                          }))}
                          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                        />
                      </label>

                      <label className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">Estadísticas Deportivas</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Logros y estadísticas visibles
                          </p>
                        </div>
                        <input
                          type="checkbox"
                          checked={athleteInfo.privacySettings.statsVisible}
                          onChange={(e) => setAthleteInfo(prev => ({
                            ...prev,
                            privacySettings: { ...prev.privacySettings, statsVisible: e.target.checked }
                          }))}
                          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                        />
                      </label>

                      <label className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">Galería Multimedia</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Fotos y videos visibles para otros
                          </p>
                        </div>
                        <input
                          type="checkbox"
                          checked={athleteInfo.privacySettings.mediaVisible}
                          onChange={(e) => setAthleteInfo(prev => ({
                            ...prev,
                            privacySettings: { ...prev.privacySettings, mediaVisible: e.target.checked }
                          }))}
                          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                        />
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Subir Contenido
              </h3>
              <button
                onClick={() => setShowUploadModal(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Tipo de Contenido
                </label>
                <div className="flex space-x-4">
                  <button
                    onClick={() => setUploadType('photo')}
                    className={`flex items-center px-4 py-2 rounded-lg border ${
                      uploadType === 'photo'
                        ? 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                        : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    <Camera size={16} className="mr-2" />
                    Fotos
                  </button>
                  <button
                    onClick={() => setUploadType('video')}
                    className={`flex items-center px-4 py-2 rounded-lg border ${
                      uploadType === 'video'
                        ? 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                        : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    <Video size={16} className="mr-2" />
                    Videos
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Seleccionar Archivos
                </label>
                <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center">
                  <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    Arrastra archivos aquí o haz clic para seleccionar
                  </p>
                  <input
                    type="file"
                    multiple
                    accept={uploadType === 'photo' ? 'image/*' : 'video/*'}
                    onChange={handleFileUpload}
                    className="hidden"
                    id="file-upload"
                  />
                  <label
                    htmlFor="file-upload"
                    className="cursor-pointer bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors"
                  >
                    Seleccionar Archivos
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AthleteProfile;